import { useState } from "react";
import { Trophy, Edit3, Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { useCurrentUser } from "@/pages/auth/hooks/useAuth";
import { useDeleteAward } from "./hooks/useAwards";
import { useCandidateAwards } from "@/pages/candidate-profile/hooks/useCandidateProfile";
import { AwardFormModal } from "./components/AwardFormModal";
import type { AwardDetail } from "@/interface/award";

export default function AwardsTab({
  profileId,
}: {
  profileId?: number | string;
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAward, setSelectedAward] = useState<AwardDetail | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const { data: user } = useCurrentUser();
  const targetId = profileId || user?.id;

  const {
    data: candAwardsRes,
    isLoading: isCandLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidateAwards(targetId, { page: 1, page_size: 10 });
  const deleteMutation = useDeleteAward();

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const awards =
    candAwardsRes?.pages.flatMap((page: any) =>
      Array.isArray(page?.data) ? page.data : page?.data?.results || [],
    ) || [];

  const isLoading = isCandLoading && awards?.length === 0;

  const handleOpenAdd = () => {
    setSelectedAward(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (award: AwardDetail) => {
    setSelectedAward(award);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteMutation.mutateAsync(deleteTargetId);
      setDeleteTargetId(null);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Trophy className="w-5 h-5 text-purple-600 dark:text-purple-400" /> Awards & Achievements
        </h2>
        <Button
          onClick={handleOpenAdd}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-bold rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Award
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="space-y-4 max-h-[700px] overflow-y-auto pr-2 custom-scrollbar"
        >
          {awards?.map((award: AwardDetail) => {
            const titleVal = award.award_title || "";
            const issuerVal = award.issuer || "";
            const dateVal = award.award_date || "";
            const descriptionVal = award.description || "";

            return (
              <div
                key={award.id}
                className="p-5 rounded-xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors flex items-start gap-4 group relative shadow-sm"
              >
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                  <button
                    onClick={() => handleOpenEdit(award)}
                    className="p-1.5 text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 bg-white dark:bg-slate-800 hover:bg-purple-50 dark:hover:bg-purple-950/40 rounded transition-colors shadow-sm"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setDeleteTargetId(String(award.id))}
                    className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 bg-white dark:bg-slate-800 hover:bg-red-50 dark:hover:bg-red-950/40 rounded transition-colors shadow-sm"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="w-12 h-12 bg-purple-50 dark:bg-purple-950/40 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-300 shrink-0">
                  <Trophy className="w-6 h-6" />
                </div>

                <div className="flex-1 pr-16">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                    <h3 className="font-bold text-slate-900 dark:text-white text-lg">
                      {titleVal}
                    </h3>
                    {dateVal && (
                      <span className="text-xs font-bold text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-950/50 border border-purple-200/50 dark:border-purple-800/50 px-3 py-1 rounded-full whitespace-nowrap">
                        {dateVal}
                      </span>
                    )}
                  </div>
                  {issuerVal && (
                    <div className="text-slate-600 dark:text-slate-400 font-medium text-sm mb-3">
                      Issued by: {issuerVal}
                    </div>
                  )}
                  {descriptionVal && (
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed max-w-4xl whitespace-pre-line">
                      {descriptionVal}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {awards.length === 0 && (
            <div className="text-center p-12 text-slate-500 dark:text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900">
              No awards added yet.
            </div>
          )}

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
              <span className="ml-2 text-sm text-slate-500 dark:text-slate-400 font-medium">
                Loading more awards...
              </span>
            </div>
          )}
        </div>
      )}

      <AwardFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editData={selectedAward}
        profileId={targetId}
      />

      <ConfirmationDialog
        isOpen={Boolean(deleteTargetId)}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Award"
        description="Are you sure you want to remove this award?"
        confirmText="Delete"
        confirmVariant="danger"
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
